import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, getComicsByCategory, type ComicCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [filteredComics, setFilteredComics] = useState(getComicsByCategory(slug || 'all'));

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setFilteredComics(getComicsByCategory(slug));
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryLabel = currentCategory?.name || '全部';

  const categoryName = activeSlug === 'all' ? '全部漫画' : `${categoryLabel}漫画`;

  return (
    <>
      <Helmet>
        <title>{`${categoryName} - 韩漫在线免费阅读_韩漫大全热门合集`}</title>
        <meta name="description" content={`韩漫漫画网站${categoryName}分类，汇聚精选好看韩国漫画，支持韩漫在线免费阅读。韩漫大全提供丰富的${categoryLabel}热门合集，满足您的阅读需求。`} />
        <meta name="keywords" content={`${categoryName},韩漫漫画网站,好看韩国漫画,韩漫在线免费阅读,韩漫大全,热门合集`} />
        <link rel="canonical" href={`https://hanmandq.com/category/${activeSlug}`} />
        <meta property="og:title" content={`${categoryName} - 韩漫在线免费阅读_韩漫大全热门合集`} />
        <meta property="og:description" content={`韩漫漫画网站${categoryName}分类，汇聚精选好看韩国漫画，支持韩漫在线免费阅读。`} />
        <meta property="og:url" content={`https://hanmandq.com/category/${activeSlug}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${categoryName} - 韩漫在线免费阅读_韩漫大全热门合集`} />
        <meta name="twitter:description" content={`韩漫漫画网站${categoryName}分类，汇聚精选好看韩国漫画，支持韩漫在线免费阅读。`} />
      </Helmet>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />

          <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              发现最热门的<strong>{categoryLabel}</strong>类韩国漫画，<strong>韩漫在线免费阅读</strong>尽在<strong>韩漫大全</strong>
            </p>

            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
              {categories.map((cat: ComicCategory) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画内容</p>
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}